<template>
  <div class="configurations-table">
    <mi-table
      ref="miTable"
      :columns="table.columns"
      :filter="configurationsSearchString"
      :loading="areConfigurationsLoading"
      :rows="table.rows"
      :custom-header="true"
      :on-change-sort-table-column-visibility="tableCallbacks.onChangeSortTableColumnVisibility"
      :on-change-sort-table-direction="tableCallbacks.onChangeSortTableDirection"
      class="configurations-table q-px-lg q-pb-lg"
      column-sort-order="da"
      loading-label="Loading configurations. Please wait ..."
      no-data-label="No configurations found"
      row-key="id"
      hide-pagination
      sticky-header
      virtual-scroll
      striped
    >
      <template #top>
        <div class="configurations-table-header">
          <span class="configurations-table-header__title">Configurations</span>
          <div class="configurations-table-header__action-buttons">
            <mi-btn-dropdown
              v-if="configurationsSelected.length !== totalConfigurations"
              flat
              :label="selectDropdownLabel"
              class="configurations-table-header__select-button"
            >
              <div class="configurations-table-header__select-dropdown">
                <mi-btn flat @click="handleShowSelectBoxes()">
                  Select configuration(s)
                </mi-btn>

                <mi-btn flat @click="handleSelectAllCurrentPage()">
                  Select all page
                </mi-btn>

                <mi-btn
                  :disable="!hasSelectedOnCurrentPage"
                  flat
                  @click="handleDeselectCurrentPage()"
                >
                  Deselect current page
                </mi-btn>
              </div>
            </mi-btn-dropdown>
            <mi-btn
              v-else
              flat
              class="configurations-table-header__select-button"
              @click="handleDeselectAll()"
            >
              Deselect all ({{ configurationsSelected.length }})
            </mi-btn>

            <mi-btn-dropdown
              ref="saveDropdown"
              dense
              :disable="configurationsSelected.length === 0"
              label="Save"
              class="configurations-table-header__save-button"
            >
              <div class="configurations-table-header__save-dropdown">
                <mi-btn flat @click="openModal(savedListsModal.SAVE.key)"> Save as new list</mi-btn>
                <mi-btn flat @click="openModal(savedListsModal.UPDATE.key)"> Add to existing list...</mi-btn>
              </div>
            </mi-btn-dropdown>
          </div>
        </div>
      </template>

      <!-- Name column -->
      <template #body-cell-name="{ value, row }">
        <td class="text-left">
          <mi-checkbox
            v-if="showSelectBox"
            :model-value="isSelected(row.id)"
            class="q-pr-sm"
            @click="toggleSelection(row.id)"
          ></mi-checkbox>
          <span>{{ value }}</span>
        </td>
      </template>

      <!-- Description column -->
      <template #body-cell-description="{ value, row }">
        <!-- Validation status -->
        <mi-td>
          <div class="flex items-center no-wrap">
            <span
              class="configurations-table__description ellipsis-2-lines q-mr-xs relative-position text-left"
            >
              {{ value }}
            </span>
            <div class="flex no-wrap q-ml-auto">
              <!-- Open configuration button -->
              <mi-tr-action-btn icon="exit" @click="openConfiguration(row.id)">
                <mi-tooltip> Open </mi-tooltip>
              </mi-tr-action-btn>

              <!-- Delete configuration button -->
              <mi-tr-action-btn
                icon="trash"
                :disabled="!hasPermissionToDelete(row)"
                @click="openDeleteConfigurationDialog(row)"
              >
                <mi-tooltip :hidden="!hasPermissionToDelete(row) ">
                  Delete
                </mi-tooltip>
              </mi-tr-action-btn>
            </div>
          </div>
        </mi-td>
      </template>
    </mi-table>
  </div>
  <div
    v-if="!!table.rows.length"
    class="configurations-table__pagination"
  >
    <mi-pagination
      :model-value="pagination.currentPage"
      :max="pagination.totalPages"
      :max-pages="pagination.maxNavPage"
      size="14px"
      boundary-links
      direction-links
      boundary-numbers
      ellipses
      @update:model-value="handlePaginationChange"
    ></mi-pagination>
  </div>
  <!-- Configuration delete dialog -->
  <configuration-delete-dialog
    v-model="isDeleteConfigurationDialogShown"
    :configuration="configurationToDelete"
    @delete:configuration="handleConfigurationDeletedEvent"
  ></configuration-delete-dialog>

  <learn-more-pac-modal :show-modal="showLearnMoreModal" @update:show-modal="showLearnMoreModal = false">
  </learn-more-pac-modal>

  <save-new-list-modal
    :show-modal="showSaveNewListModal"
    :selected-configurations-ids-set="configurationsSelected"
    :handle-deselect-all="handleDeselectAll"
    @update:show-modal="showSaveNewListModal = false"
  >
  </save-new-list-modal>
  <add-configurations-to-list-modal
    :handle-deselect-all="handleDeselectAll"
    :selected-configurations-ids-set="configurationsSelected"
    :show-modal="showAddConfigurationsToListModal"
    @update:show-modal="showAddConfigurationsToListModal = false"
  >
  </add-configurations-to-list-modal>
</template>

<script>
  import { computed, onMounted, onUnmounted, ref, watch, getCurrentInstance } from 'vue'
  import { useStore } from 'vuex'
  import { useRouter } from 'vue-router'
  import { usePagination } from '@/composables/usePagination'
  import { useTable } from '@/composables/useTable'
  import ConfigurationDeleteDialog from '@/components/pac/configuration/dialogs/ConfigurationDeleteDialog.vue'
  import MiPagination from '@/packages/@mi-library/MiPagination/MiPagination.vue'
  import { getConfigurations } from '@/api'
  import { configurationsTableColumns } from '@/components/pac/configurations/utils'
  import { PAC_DELETED_CONFIG } from '@/utils/analytics/constants'
  import { PAC_CONFIGURATION_ROUTE } from '@/router/routeNames'
  import { SET_CONFIGURATIONS_SEARCH_STRING } from '@/store/modules/pac/mutationTypes'
  import recordAnalytics from '@/utils/analytics/recordAnalytics'
  import MiBtnDropdown from '@/packages/@mi-library/MiBtnDropdown/MiBtnDropdown.vue'
  import MiBtn from '@/packages/@mi-library/MiBtn/MiBtn.vue'
  import MiCheckbox from '@/packages/@mi-library/MiCheckbox/MiCheckbox.vue'
  import SaveNewListModal from '@/components/pac/saved-lists/SaveNewListModal.vue'
  import AddConfigurationsToListModal from '@/components/pac/saved-lists/AddConfigurationsToListModal.vue'
  import LearnMorePacModal from '../LearnMorePacModal.vue'

  export default {
    name: 'ConfigurationsTable',
    components: {
      AddConfigurationsToListModal,
      SaveNewListModal,
      MiCheckbox,
      MiBtn,
      MiBtnDropdown,
      LearnMorePacModal,
      MiPagination,
      ConfigurationDeleteDialog
    },
    setup() {
      const areConfigurationsLoading = ref(false)
      const configurationToDelete = ref(null)
      const isDeleteConfigurationDialogShown = ref(false)
      const { proxy } = getCurrentInstance()
      const showLearnMoreModal = ref(
        proxy.$q.localStorage.getItem(process.env.VUE_APP_STORAGE_KEY_LEARN_MORE_PAC_MODAL) === null
      )

      const savedListsModal = { SAVE: { key: 'saveNew' }, UPDATE: { key: 'addConfigurations' } }

      const showSelectBox = ref(false)
      const configurationsSelected = ref([])
      const selectedSet = computed(() => new Set(configurationsSelected.value))
      const showSaveNewListModal = ref(false)
      const showAddConfigurationsToListModal = ref(false)

      const totalConfigurations = ref(0)

      const { pagination, handleChange: handlePaginationChange } = usePagination({ pageSize: 20 })
      const { table, callbacks: tableCallbacks } = useTable(configurationsTableColumns)

      const router = useRouter()
      const { state: { user, pac: pacStore }, commit } = useStore()
      const usePacMutation = name => `pac/${ name }`
      const configurationsSearchString = computed(() => pacStore.configurationsSearchString)

      const currentPageIds = computed(() => table.rows.map(r => r.id))
      const currentPageCount = computed(() => currentPageIds.value.length)

      const selectedOnCurrentPageCount = computed(() => {
        const currentPageIdSet = new Set(currentPageIds.value)
        const selectedOnCurrentPage = configurationsSelected.value.filter(id => currentPageIdSet.has(id))
        return selectedOnCurrentPage.length
      })

      const saveDropdown = ref(null)

      const openModal = modalToOpen => {
        modalToOpen === savedListsModal.SAVE.key
          ? showSaveNewListModal.value = true : showAddConfigurationsToListModal.value = true
        saveDropdown?.value.hide()
      }

      const hasSelectedOnCurrentPage = computed(() => selectedOnCurrentPageCount.value > 0)
      const hasAnySelected = computed(() => configurationsSelected.value.length > 0)

      const selectDropdownLabel = computed(() => {
        if (hasAnySelected.value) return `Deselect (${ configurationsSelected.value.length })`
        return 'Select'
      })

      const hasPermissionToDelete = configuration => {
        const { authorizedUsers = [] } = configuration
        if (authorizedUsers.length) return authorizedUsers.includes(user.username)
        return true
      }

      const isSelected = id => selectedSet.value.has(id)

      const addSelection = id => {
        if (!selectedSet.value.has(id)) configurationsSelected.value = [...configurationsSelected.value, id]
      }

      const removeSelection = id => {
        if (selectedSet.value.has(id)) {
          configurationsSelected.value = configurationsSelected.value.filter(
            configSelectedId => configSelectedId !== id
          )
        }
      }

      const toggleSelection = id => {
        showSelectBox.value = true
        if (selectedSet.value.has(id)) removeSelection(id)
        else addSelection(id)
      }

      const handleShowSelectBoxes = () => {
        showSelectBox.value = true
      }

      const handleSelectAllCurrentPage = () => {
        showSelectBox.value = true
        const configurationsSelectedSet = new Set(configurationsSelected.value)
        currentPageIds.value.forEach(id => configurationsSelectedSet.add(id))
        configurationsSelected.value = Array.from(configurationsSelectedSet)
      }

      const handleDeselectCurrentPage = () => {
        const currentPageIdsSet = new Set(currentPageIds.value)
        configurationsSelected.value = configurationsSelected.value.filter(id => !currentPageIdsSet.has(id))
      }

      const handleDeselectAll = (hideSelectBoxes = false) => {
        configurationsSelected.value = []
        if (hideSelectBoxes) showSelectBox.value = false
      }

      const buildUrlSearchParams = () => {
        const params = new URLSearchParams()
        params.append('page', pagination.currentPage - 1)
        params.append('size', pagination.pageSize)
        params.append('sortBy', table.sort?.sortBy || 'modifiedDate')
        params.append('sortDirection', table.sort?.sortDirection || 'DESC')
        params.append('excludeFields', 'windChillData')
        params.append('excludeFields', 'ignoreList')
        params.append('excludeFields', 'restorePoints')
        params.append('excludeFields', 'selectedChoices')

        configurationsSearchString.value && params.append('search', configurationsSearchString.value)

        return params.toString()
      }

      const fetchConfigurationResults = async () => {
        areConfigurationsLoading.value = true
        const params = buildUrlSearchParams()

        const fetchConfigurationResultsPromise = () => getConfigurations(params)
        const data = await tableCallbacks.fetchTableData(fetchConfigurationResultsPromise)

        pagination.totalPages = data.totalPages || 1
        totalConfigurations.value = data.totalElements
        areConfigurationsLoading.value = false
      }

      const handleConfigurationDeletedEvent = async () => {
        await fetchConfigurationResults()
        recordAnalytics(PAC_DELETED_CONFIG)
      }

      const openDeleteConfigurationDialog = configuration => {
        configurationToDelete.value = { id: configuration.id, name: configuration.name }
        isDeleteConfigurationDialogShown.value = true
      }

      const openConfiguration = (id = '') => {
        router.push({ name: PAC_CONFIGURATION_ROUTE.name, params: { id } })
      }

      watch(() => pagination.currentPage, () => {
        fetchConfigurationResults()
      })

      watch(() => [configurationsSearchString.value, table.sort], () => {
        pagination.currentPage === 1 ? fetchConfigurationResults() : handlePaginationChange(1)
      })

      onMounted(async () => {
        await fetchConfigurationResults()
      })

      onUnmounted(() => {
        commit(usePacMutation(SET_CONFIGURATIONS_SEARCH_STRING), '')
      })

      return {
        table,
        pagination,
        handlePaginationChange,
        areConfigurationsLoading,
        handleConfigurationDeletedEvent,
        configurationToDelete,
        openDeleteConfigurationDialog,
        isDeleteConfigurationDialogShown,
        openConfiguration,
        configurationsSearchString,
        tableCallbacks,
        hasPermissionToDelete,
        showLearnMoreModal,
        showAddConfigurationsToListModal,
        showSelectBox,
        configurationsSelected,
        isSelected,
        toggleSelection,
        handleShowSelectBoxes,
        handleSelectAllCurrentPage,
        handleDeselectCurrentPage,
        handleDeselectAll,
        currentPageCount,
        selectedOnCurrentPageCount,
        hasSelectedOnCurrentPage,
        hasAnySelected,
        selectDropdownLabel,
        totalConfigurations,
        showSaveNewListModal,
        buildUrlSearchParams,
        savedListsModal,
        openModal,
        saveDropdown
      }
    }
  }
  </script>

<style lang="scss" scoped>
  .configurations-table {
    min-height: 50vh;
    max-height: 75vh;

    &__pagination {
      display: flex;
      justify-content: center;
      margin-bottom: 20px;
    }

    &__description {
      white-space: normal;
    }

    // stylelint-disable declaration-no-important
    .mi-btn.q-btn:not(.q-btn--flat):not(.q-btn--outline):not(.configurations-table-header__save-button).disabled,
    .mi-btn.q-btn:not(.q-btn--flat):not(.q-btn--outline):not(.configurations-table-header__save-button)[disabled] {
      opacity: 0 !important;
    }

    tr:hover .mi-btn.q-btn:not(.q-btn--flat):not(.q-btn--outline).disabled,
    tr:hover .mi-btn.q-btn:not(.q-btn--flat):not(.q-btn--outline)[disabled] {
      opacity: 1 !important;
    }

    .mi-btn.q-btn:not(.q-btn--flat):not(.q-btn--outline).disabled:hover,
    .mi-btn.q-btn:not(.q-btn--flat):not(.q-btn--outline)[disabled]:hover {
      background-color: $mi-silver-200 !important;
    }
  }

  .configurations-table-header {
    width: 100%;
    justify-content: space-between;
    display: flex;

    &__action-buttons {
      gap: 12px;
      display: flex;
    }

    &__select-dropdown,
    &__save-dropdown {
      display: flex;
      flex-direction: column;

      .mi-btn {
        font-family: "Man Europe", sans-serif;
        font-weight: 400;
        font-size: 14px;
        line-height: 150%;
        align-items: start;
        width: 100%;
      }
    }

    &__select-button {
      border: 2px solid #303c49;
      width: 100%;
      height: 40px;
      justify-content: center;
      font-size: 14px;
    }

    &__save-button {
      width: 87px;
      height: 40px;
      justify-content: center;

      ::v-deep(.q-btn__content) {
        align-items: anchor-center !important;
      }
    }

    &__title {
      font-family: "Man Europe", sans-serif;
      font-weight: 700;
      font-size: 20px;
      line-height: 30px;
      letter-spacing: 0;
      text-transform: capitalize;
    }
  }
</style>
